import queryClient from '../../../utils/queryClient';
import sportsApi from '../../../utils/sportsApi';
import { CommonKey } from '../../../types/api';

const STALE_TIME_MS = 240_000;

export class MediaValueReportRequestError extends Error {
    constructor(urlSuffix: string) {
        super(`media-value-reports request failed${urlSuffix}`);
        this.name = 'MediaValueReportRequestError';
    }
}

export const isMediaValueReportRequestError = (
    error: unknown,
): error is MediaValueReportRequestError =>
    error instanceof MediaValueReportRequestError;

// sportsApi's HTTP-error catchers resolve the chain with the wretch request
// object instead of rejecting, so a failed report request would otherwise be
// indistinguishable from a successful empty one (and get cached as such).
const isWretchRequestObject = (result: unknown): boolean =>
    typeof result === 'object' &&
    result !== null &&
    '_url' in result &&
    '_options' in result;

const fetchMediaValueReport = async (
    query: object,
    urlSuffix = '',
): Promise<unknown> => {
    const result = await sportsApi
        .url(`/media-value-reports${urlSuffix}`)
        .json(query)
        .post()
        .json();

    if (isWretchRequestObject(result)) {
        throw new MediaValueReportRequestError(urlSuffix);
    }

    return result;
};

const fetchMediaValueReportCached = (
    query: object,
    urlSuffix = '',
): Promise<unknown> =>
    queryClient.fetchQuery({
        queryKey: [CommonKey.MediaValueReport, urlSuffix, query],
        queryFn: () => fetchMediaValueReport(query, urlSuffix),
        staleTime: STALE_TIME_MS,
        // Report queries can take minutes; automatic retries would multiply
        // the wait. Failures also must not be cached — throwing (instead of
        // resolving empty) keeps the query in error state so the next call
        // refetches rather than serving a poisoned success.
        retry: false,
    });

export default fetchMediaValueReportCached;
